import React, { useRef, useEffect } from 'react';
import { Lock, Pencil, Check } from 'lucide-react';

interface ProfileInputFieldProps {
  id?: string;
  label: string;
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  isReadOnly?: boolean;
  isEditing?: boolean;
  onToggleEdit?: () => void;
  type?: string;
}

export const ProfileInputField: React.FC<ProfileInputFieldProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  isReadOnly = false,
  isEditing = false,
  onToggleEdit,
  type = 'text',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const elementId = id || `input-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={elementId}
        className="block text-[11px] font-black text-slate-500 uppercase tracking-wider"
      >
        {label}
      </label>

      <div className="relative flex items-center">
        {isReadOnly ? (
          <>
            <input
              id={elementId}
              type={type}
              disabled
              value={value || ''}
              placeholder={placeholder}
              className="w-full pl-3.5 pr-10 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-xs sm:text-sm font-semibold text-slate-500 cursor-not-allowed select-none outline-none"
            />
            <div className="absolute right-3 flex items-center pointer-events-none text-slate-400">
              <Lock className="w-4 h-4 text-slate-400" />
            </div>
          </>
        ) : isEditing ? (
          <>
            <input
              ref={inputRef}
              id={elementId}
              type={type}
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder={placeholder}
              className="w-full pl-3.5 pr-10 py-2.5 bg-white border-2 border-sky-500 ring-2 ring-sky-100 rounded-xl text-xs sm:text-sm font-bold text-slate-900 shadow-xs outline-none transition-all"
            />
            <button
              type="button"
              onClick={onToggleEdit}
              title="Confirmar edición"
              className="absolute right-2 p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
            >
              <Check className="w-4 h-4 stroke-[2.5]" />
            </button>
          </>
        ) : (
          <>
            <input
              id={elementId}
              type={type}
              readOnly
              onClick={onToggleEdit}
              value={value || ''}
              placeholder={placeholder}
              className="w-full pl-3.5 pr-10 py-2.5 bg-slate-200/80 hover:bg-slate-200 border border-slate-300/80 rounded-xl text-xs sm:text-sm font-bold text-slate-800 cursor-pointer outline-none transition-colors"
            />
            <button
              type="button"
              onClick={onToggleEdit}
              title="Hacer clic para editar"
              className="absolute right-2 p-1.5 rounded-lg hover:bg-slate-300/60 text-slate-600 hover:text-sky-700 transition-colors cursor-pointer"
            >
              <Pencil className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
